import { Button, Checkbox, Form } from '@heroui/react'
import {
  CreatePlanDto,
  QpaServiceResponseDto,
  useCreatePlan,
  useGetStripeProducts,
  usePatchPlan,
} from '@qse/api'
import { type ModalComponentProps } from '@qse/constants'
import { Alert } from '@qse/ui'
import { useCallback, useEffect, useState } from 'react'

import HeroControl from '../../hero-input'

// ─── QPA service category definitions ─────────────────────────────────────────
// Extensible: add new services to the relevant category, or add new categories.

type QpaServiceKey = 'CBOM' | 'SBOM' | 'HBOM' | 'BUILDER' | 'PQC' | 'CSF' | 'NCSB' | 'PQCMM' | 'ACT'

interface QpaServiceDefinition {
  key: QpaServiceKey
  label: string
}

interface QpaServiceCategory {
  label: string
  services: QpaServiceDefinition[]
}

const QPA_SERVICE_CATEGORIES: QpaServiceCategory[] = [
  {
    label: 'Inventory',
    services: [
      { key: 'SBOM', label: 'Application' },
      { key: 'CBOM', label: 'Cryptography' },
      { key: 'HBOM', label: 'Hardware' },
      { key: 'BUILDER', label: 'Builder' },
    ],
  },
  {
    label: 'Framework',
    services: [
      { key: 'PQC', label: 'PQC' },
      { key: 'NCSB', label: 'NCSB' },
      { key: 'CSF', label: 'CSF' },
      { key: 'PQCMM', label: 'PQCMM' },
      { key: 'ACT', label: 'ACT854' },
    ],
  },
]

const ALL_SERVICE_KEYS: QpaServiceKey[] = QPA_SERVICE_CATEGORIES.flatMap(cat =>
  cat.services.map(s => s.key),
)

function buildInitialServiceState(
  existingServices?: Array<QpaServiceResponseDto>,
): Record<QpaServiceKey, boolean> {
  const state: Record<string, boolean> = {}

  ALL_SERVICE_KEYS.forEach(key => {
    state[key] = false
  })
  if (existingServices) {
    existingServices.forEach(s => {
      if (s.serviceKey in state) {
        state[s.serviceKey] = true
      }
    })
  }

  return state as Record<QpaServiceKey, boolean>
}

// ──────────────────────────────────────────────────────────────────────────────

export default function CreatePlanModal(props: ModalComponentProps) {
  const { mutateAsync: createPlan, isPending } = useCreatePlan()
  const { mutateAsync: updatePlan, isPending: isUpdating } = usePatchPlan()
  const { setModelActive, onClose, data } = props
  const { data: stripeProducts } = useGetStripeProducts()
  const action = data?.data?.action
  const [error, setError] = useState<string | null>(null)
  const [planAudience, setPlanAudience] = useState<'QSE Retail' | 'QSE Enterprise' | ''>(
    data?.data?.planInfo?.audience || '',
  )
  const [validationErrors, setValidationErrors] = useState<{
    name?: string
    description?: string
    serviceUsageQuota?: string
    storageQuotaGb?: string
    audience?: string
    stripePlanId?: string
    qpaSeedCount?: string
  }>({})
  const isEdit = action === 'edit'
  const planInfo = data?.data?.planInfo

  // Use max values from API response (backend now calculates these)
  const maxApiQuota = planInfo?.maxApiQuota ?? 0
  const maxStorageQuotaGb = planInfo?.maxStorageQuotaGb ?? 0
  const minServiceUsage = Math.max(1, maxApiQuota)
  const minStorageGb = Math.max(1, Math.ceil(maxStorageQuotaGb))
  const minQpaSeedCount = Math.max(0, planInfo?.qpaSeedCount ?? 0)

  // controlled values so helper text updates live
  const [planeName, setPlaneName] = useState<string>(planInfo?.name || '')
  const [description, setDescription] = useState<string>(planInfo?.description || '')
  const [serviceUsage, setServiceUsage] = useState<number>(Number(planInfo?.serviceUsageQuota) || 1)
  const [storageGb, setStorageGb] = useState<number>(Number(planInfo?.storageQuotaGb) || 1)
  const [qpaSeedCount, setQpaSeedCount] = useState<number>(Number(planInfo?.qpaSeedCount) || 0)
  const [stripePlanId, setStripePlanId] = useState<string>(planInfo?.stripePlanId || '')

  // QPA feature state
  const [qpaEnabled, setQpaEnabled] = useState<boolean>(
    isEdit ? (Number(planInfo?.qpaSeedCount) ?? 0) > 0 : false,
  )
  const [qpaServices, setQpaServices] = useState<Record<QpaServiceKey, boolean>>(() =>
    buildInitialServiceState(planInfo?.qpaServices),
  )

  // Validation functions
  const validateServiceUsage = useCallback(
    (value: number) => {
      if (!value) return 'Service Usage Quota is required'
      if (value < 0) return 'Service Usage Quota must be greater than 0'
      if (Number(value) < minServiceUsage)
        return `⚠️ Minimum: ${minServiceUsage} (based on current tenant usage)`

      return undefined
    },
    [minServiceUsage],
  )

  const validateStorageQuota = useCallback(
    (value: number) => {
      if (!value) {
        return 'Storage Quota is required'
      }
      if (value < 0) return 'Storage Quota must be greater than 0'
      if (Number(value) < minStorageGb)
        return `⚠️ Minimum: ${minStorageGb} GB (based on current tenant usage)`

      return undefined
    },
    [minStorageGb],
  )

  const validateQpaSeedCount = useCallback(
    (value: number) => {
      if (qpaEnabled) {
        if (!value) {
          return 'QPA License Count is required'
        }
        if (value < 0) return 'QPA License Count must be greater than 0'
        if (Number(value) < minQpaSeedCount)
          return `⚠️ Minimum: ${minQpaSeedCount} (based on current tenant usage)`
      }
    },
    [minQpaSeedCount, qpaEnabled],
  )

  // Handle real-time validation
  const handleNameChange = (value: string) => {
    setPlaneName(value)
    const error = validateName(value)

    setValidationErrors(prev => ({ ...prev, name: error }))
    // Clear form-level error when user corrects the name
    setError(null)
  }

  const handleDescriptionChange = (value: string) => {
    setDescription(value)
    const error = validateDescription(value)

    setValidationErrors(prev => ({ ...prev, description: error }))
    // Clear form-level error when user makes changes
    setError(null)
  }

  const handleServiceUsageChange = (val: number) => {
    setServiceUsage(Number(val))
    const error = validateServiceUsage(Number(val))

    setValidationErrors(prev => ({ ...prev, serviceUsageQuota: error }))
    // Clear form-level error when user makes changes
    setError(null)
  }

  const handleStorageQuotaChange = (val: number) => {
    setStorageGb(Number(val))
    const error = validateStorageQuota(Number(val))

    setValidationErrors(prev => ({ ...prev, storageQuotaGb: error }))
    // Clear form-level error when user makes changes
    setError(null)
  }

  const validateName = (value: string) => {
    if (!value) {
      return 'Plan Name is required'
    }
    if (value.length > 255) {
      return 'Plan name must be 255 characters or fewer.'
    }
  }

  const validateDescription = (value: string) => {
    if (value && value.length > 1000) {
      return 'Description must be 1000 characters or fewer.'
    }
  }

  const handleQpaSeedCountChange = (val: number) => {
    setQpaSeedCount(Number(val))
    const error = validateQpaSeedCount(Number(val))

    setValidationErrors(prev => ({ ...prev, qpaSeedCount: error }))
    // Clear form-level error when user makes changes
    setError(null)
  }

  const handleQpaEnabledChange = (checked: boolean) => {
    setQpaEnabled(checked)
    if (!checked) {
      setQpaSeedCount(0)
      setQpaServices(buildInitialServiceState())
      setValidationErrors(prev => ({ ...prev, qpaSeedCount: undefined }))
    }
    setError(null)
  }

  const handleServiceToggle = (key: QpaServiceKey, checked: boolean) => {
    setQpaServices(prev => ({ ...prev, [key]: checked }))
  }

  // Comprehensive validation function
  const validateForm = () => {
    const errors: typeof validationErrors = {}

    // Validate name
    const nameError = validateName(planeName)

    if (nameError) errors.name = nameError

    // Validate description
    const descriptionError = validateDescription(description)

    if (descriptionError) errors.description = descriptionError

    // Validate service usage
    const serviceError = validateServiceUsage(serviceUsage)

    if (serviceError) errors.serviceUsageQuota = serviceError

    // Validate storage quota
    const storageError = validateStorageQuota(storageGb)

    if (storageError) errors.storageQuotaGb = storageError

    if (qpaEnabled) {
      const qpaSeedCountError = validateQpaSeedCount(qpaSeedCount)

      if (qpaSeedCountError) errors.qpaSeedCount = qpaSeedCountError
    }

    // Validate audience (only for create mode)
    if (!isEdit && !planAudience) {
      errors.audience = 'Plan Audience is required'
    }

    // Validate stripe plan (only for QSE Retail and only in create mode)
    // In edit mode, skip validation to allow legacy plans without stripe plan ID
    if (!isEdit && planAudience === 'QSE Retail' && !stripePlanId) {
      errors.stripePlanId = 'Stripe Plan ID is required for QSE Retail'
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    }
  }

  // Sync state with planInfo when modal opens with different data
  useEffect(() => {
    if (planInfo) {
      setPlaneName(planInfo.name || '')
      setDescription(planInfo.description || '')
      setServiceUsage(Number(planInfo.serviceUsageQuota) || 1)
      setStorageGb(Number(planInfo.storageQuotaGb) || 1)
      setStripePlanId(planInfo.stripePlanId || '')
      setPlanAudience(planInfo.audience || '')
      const seedCount = Number(planInfo.qpaSeedCount) || 0

      setQpaSeedCount(seedCount)
      setQpaEnabled(seedCount > 0)
      setQpaServices(buildInitialServiceState(planInfo.qpaServices))
    }
  }, [planInfo])

  // Run initial validation on mount when in edit mode
  useEffect(() => {
    if (isEdit && planInfo) {
      const nameError = validateName(planInfo.name)
      const descriptionError = validateDescription(planInfo.description || '')
      const serviceError = validateServiceUsage(Number(planInfo.serviceUsageQuota))
      const storageError = validateStorageQuota(Number(planInfo.storageQuotaGb))
      const qpaSeedCountError = validateQpaSeedCount(Number(planInfo.qpaSeedCount))

      setValidationErrors({
        name: nameError,
        description: descriptionError,
        serviceUsageQuota: serviceError,
        storageQuotaGb: storageError,
        qpaSeedCount: qpaSeedCountError,
      })
    }
  }, [isEdit, planInfo, validateServiceUsage, validateStorageQuota, validateQpaSeedCount])

  const buildQpaServicesPayload = () =>
    ALL_SERVICE_KEYS.map(key => ({ serviceKey: key, enabled: qpaServices[key] }))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Run comprehensive validation
    const validation = validateForm()

    if (!validation.isValid) {
      // Update validation errors state to show all errors
      setValidationErrors(prev => ({ ...prev, ...validation.errors }))

      return
    }

    const formData = new FormData(e.target as HTMLFormElement)
    const values = Object.fromEntries(formData.entries()) as unknown as CreatePlanDto

    try {
      if (isEdit) {
        // Get original data for comparison
        const originalData = data?.data?.planInfo

        if (!originalData) {
          setError('Original plan data not found')

          return
        }

        // Format new data
        // Note: Use state value for stripePlanId because disabled fields don't submit with FormData
        const newData = {
          name: values.name,
          description: values.description || '',
          serviceUsageQuota: Number(values.serviceUsageQuota),
          storageQuotaGb: Number(values.storageQuotaGb),
          stripePlanId: stripePlanId || '',
          isActive: true,
          qpaSeedCount: qpaEnabled ? Number(qpaSeedCount) : 0,
          qpaServices: buildQpaServicesPayload(),
        }

        // Compare with original data and only include changed fields
        const changes: Partial<CreatePlanDto> = {}

        if (newData.name !== originalData.name) changes.name = newData.name
        if (newData.description !== (originalData.description || ''))
          changes.description = newData.description
        if (newData.serviceUsageQuota !== originalData.serviceUsageQuota)
          changes.serviceUsageQuota = newData.serviceUsageQuota
        if (newData.storageQuotaGb !== originalData.storageQuotaGb)
          changes.storageQuotaGb = newData.storageQuotaGb
        if (newData.stripePlanId !== (originalData.stripePlanId || ''))
          changes.stripePlanId = newData.stripePlanId
        if (newData.qpaSeedCount !== originalData.qpaSeedCount)
          changes.qpaSeedCount = newData.qpaSeedCount

        // Always include qpaServices on edit so removals are persisted
        changes.qpaServices = newData.qpaServices

        if (Object.keys(changes).length === 0) {
          // No changes detected, just close the modal
          setModelActive(undefined)
          onClose()

          return
        }

        // Send only the changed data
        await updatePlan(
          { id: data?.data?.planInfo?.id, data: changes },
          {
            onError: error => {
              setError(error.message)
            },
          },
        )
      } else {
        // Format data for API according to CreatePlanDto
        // Note: Use state value for stripePlanId to ensure we get the selected value
        const formattedData = {
          name: values.name,
          description: values.description,
          audience: planAudience as 'QSE Retail' | 'QSE Enterprise',
          serviceUsageQuota: values.serviceUsageQuota,
          storageQuotaGb: values.storageQuotaGb,
          stripePlanId: stripePlanId || '',
          isActive: true,
          qpaSeedCount: qpaEnabled ? Number(qpaSeedCount) : 0,
          qpaServices: qpaEnabled ? buildQpaServicesPayload() : [],
        }

        await createPlan(formattedData, {
          onError: error => {
            setError(error.message)
          },
        })
      }

      // Reset form and close modal on success
      setModelActive(undefined)
      onClose()
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred'

      setError(errorMessage)
    } finally {
      // setSubmitting(false)
    }
  }

  const handleCancel = () => {
    setModelActive(undefined)
    onClose()
  }

  // Handle submit button click to catch validation before form submission
  const handleSubmitClick = () => {
    const validation = validateForm()

    if (!validation.isValid) {
      // Update validation errors state to show all errors
      setValidationErrors(prev => ({ ...prev, ...validation.errors }))

      return
    }
  }

  return (
    <div>
      <Form
        className="flex flex-col gap-5 py-5"
        validationBehavior="native"
        onSubmit={handleSubmit}
      >
        <div className="flex flex-col gap-4 w-full">
          {error && <Alert color="danger" description={error} />}
          <HeroControl
            isRequired
            control="text"
            defaultValue={data?.data?.planInfo?.name}
            errorMessage={validationErrors.name}
            isInvalid={!!validationErrors.name}
            label="Plan Name"
            name="name"
            placeholder="Enter plan name"
            type="text"
            value={planeName}
            validate={(value: string) => {
              if (!value) {
                return 'Plan Name is required'
              }
              if (value.length > 255) {
                return 'Plan name must be 255 characters or fewer.'
              }
            }}
            onChange={handleNameChange}
          />

          <HeroControl
            control="textarea"
            defaultValue={data?.data?.planInfo?.description}
            errorMessage={validationErrors.description}
            isInvalid={!!validationErrors.description}
            label="Description"
            max={1000}
            name="description"
            placeholder="Enter plan description"
            value={description}
            validate={(value: string) => {
              if (value && value.length > 1000) {
                return 'Description must be 1000 characters or fewer.'
              }
            }}
            onChange={handleDescriptionChange}
          />

          <HeroControl
            isRequired
            control="select"
            defaultSelectedKeys={[data?.data?.planInfo?.audience || planAudience]}
            isDisabled={isEdit}
            label="Plan Audience"
            name="audience"
            placeholder="Select plan audience"
            options={[
              { key: 'QSE Retail', label: 'QSE Retail' },
              { key: 'QSE Enterprise', label: 'QSE Enterprise' },
            ]}
            validate={(value: string) => {
              if (!value) {
                return 'Plan Audience is required'
              }
            }}
            onChange={(value: string) => {
              setPlanAudience(value as 'QSE Retail' | 'QSE Enterprise')
              // Clear stripe plan ID when QSE Enterprise is selected (not needed)
              if (value === 'QSE Enterprise') {
                setStripePlanId('')
              }
              // Clear stripe plan ID validation error when changing audience
              if (validationErrors.stripePlanId) {
                setValidationErrors(prev => ({ ...prev, stripePlanId: undefined }))
              }
              // Clear form-level error when user makes changes
              setError(null)
            }}
          />

          <HeroControl
            key={planAudience}
            control="select"
            defaultSelectedKeys={[data?.data?.planInfo?.stripePlanId]}
            errorMessage={validationErrors.stripePlanId}
            isClearable={planAudience !== 'QSE Retail'}
            isInvalid={!!validationErrors.stripePlanId}
            isRequired={!isEdit && planAudience === 'QSE Retail'}
            label={planAudience === 'QSE Retail' ? 'Stripe Plan ID' : 'Stripe Plan ID'}
            name="stripePlanId"
            isDisabled={
              planAudience === 'QSE Enterprise' ||
              (isEdit && planAudience === 'QSE Retail') ||
              !planAudience
            }
            options={
              stripeProducts
                ? stripeProducts?.products?.map(product => ({
                    label: product?.name,
                    key: product?.id,
                  }))
                : []
            }
            placeholder={
              planAudience
                ? planAudience === 'QSE Retail'
                  ? isEdit
                    ? 'Select Stripe plan (optional)'
                    : 'Select Stripe plan (required)'
                  : 'Not applicable for Enterprise plans'
                : 'Select Stripe plan'
            }
            validate={(value: string) => {
              // Only require stripe plan ID for QSE Retail in create mode
              // In edit mode, allow legacy plans without stripe plan ID
              if (!isEdit && planAudience === 'QSE Retail' && !value) {
                return 'Stripe Plan ID is required'
              }
            }}
            onChange={(value: string) => {
              setStripePlanId(value)
              // Clear validation error when value is selected
              if (value && validationErrors.stripePlanId) {
                setValidationErrors(prev => ({ ...prev, stripePlanId: undefined }))
              }
              // Clear form-level error when user makes changes
              setError(null)
            }}
          />

          <HeroControl
            isRequired
            control="number"
            defaultValue={planInfo?.serviceUsageQuota}
            errorMessage={validationErrors.serviceUsageQuota}
            isInvalid={!!validationErrors.serviceUsageQuota}
            label="Service Usage Quota"
            minValue={1}
            name="serviceUsageQuota"
            placeholder="Enter service usage limit"
            step={1}
            type="number"
            value={serviceUsage as unknown as string}
            validate={(v: number) => {
              if (!v && v !== 0) return 'Service Usage Quota is required'
              if (Number(v) < minServiceUsage)
                return `⚠️ Minimum: ${minServiceUsage} (based on current tenant usage)`
            }}
            onTypeChange={handleServiceUsageChange}
          />

          <HeroControl
            isRequired
            control="number"
            defaultValue={planInfo?.storageQuotaGb}
            errorMessage={validationErrors.storageQuotaGb}
            isInvalid={!!validationErrors.storageQuotaGb}
            label="Storage Quota (GB)"
            minValue={1}
            name="storageQuotaGb"
            placeholder="Enter storage limit in GB"
            step={1}
            type="number"
            value={storageGb as unknown as string}
            validate={(value: number) => {
              if (!value) {
                return 'Storage Quota is required'
              }
              if (Number(value) < minStorageGb)
                return `⚠️ Minimum: ${minStorageGb} GB (based on current tenant usage)`
            }}
            onTypeChange={handleStorageQuotaChange}
          />

          {/* QPA Section */}
          <div className="flex flex-col gap-3">
            <Checkbox isSelected={qpaEnabled} onValueChange={handleQpaEnabledChange}>
              <span className="text-sm font-medium">Enable QPA (Quantum Posture Assessment)</span>
            </Checkbox>

            {qpaEnabled && (
              <div className="flex flex-col gap-4 pl-6 border-l-2 border-default-200">
                <HeroControl
                  isRequired
                  control="number"
                  defaultValue={planInfo?.qpaSeedCount}
                  errorMessage={validationErrors.qpaSeedCount}
                  isInvalid={!!validationErrors.qpaSeedCount}
                  label="QPA License Count"
                  minValue={1}
                  name="qpaSeedCount"
                  placeholder="Enter QPA license count"
                  step={1}
                  type="number"
                  value={qpaSeedCount as unknown as string}
                  validate={(value: number) => {
                    if (!value) {
                      return 'QPA License Count is required'
                    }
                    if (Number(value) < minQpaSeedCount)
                      return `⚠️ Minimum: ${minQpaSeedCount} (based on current tenant usage)`
                  }}
                  onTypeChange={handleQpaSeedCountChange}
                />

                {/* QPA Services */}
                <div className="flex flex-col gap-3">
                  <p className="text-sm font-semibold text-default-700">QPA Services</p>

                  {QPA_SERVICE_CATEGORIES.map(category => (
                    <div key={category.label} className="flex flex-col gap-2">
                      <p className="text-xs font-medium text-default-500 uppercase tracking-wide">
                        {category.label}
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        {category.services.map(service => (
                          <Checkbox
                            key={service.key}
                            isSelected={qpaServices[service.key]}
                            onValueChange={checked => handleServiceToggle(service.key, checked)}
                          >
                            <span className="text-sm">{service.label}</span>
                            <span className="text-xs text-default-400 ml-1">({service.key})</span>
                          </Checkbox>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 w-full">
          <Button radius="sm" variant="light" onPress={handleCancel}>
            Cancel
          </Button>
          <Button
            color="primary"
            isLoading={isPending || isUpdating}
            radius="sm"
            type="submit"
            onPress={handleSubmitClick}
          >
            {isEdit ? 'Update Plan' : 'Create Plan'}
          </Button>
        </div>
      </Form>
    </div>
  )
}
